import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import {
  isAppliedPatronLoyaltyReward,
  PatronCheckoutLoyaltyRewards,
} from "@/features/Loyalty/components/PatronCheckoutLoyaltyRewards/PatronCheckoutLoyaltyRewards";
import { useFlags } from "@/utils/launchDarkly/useFlags";

import type { BookingLoyaltyRewards_booking$key } from "./__generated__/BookingLoyaltyRewards_booking.graphql";

type Props = {
  bookingRef: BookingLoyaltyRewards_booking$key | null;
  display?: "all" | "rewards" | "enrollment";
  section?: "all" | "applied" | "action";
};

export const BookingLoyaltyRewards = ({
  bookingRef,
  display = "all",
  section = "all",
}: Props) => {
  return (
    <BookingLoyaltyRewardsFragment
      bookingRef={bookingRef}
      display={display}
      section={section}
    />
  );
};

const BookingLoyaltyRewardsFragment = ({
  bookingRef,
  display,
  section,
}: {
  bookingRef: BookingLoyaltyRewards_booking$key | null;
  display: NonNullable<Props["display"]>;
  section: NonNullable<Props["section"]>;
}) => {
  const booking = useFragment(BookingLoyaltyRewardsBookingFragment, bookingRef);
  const { patronLoyalty } = useFlags();
  const ownerOrder = booking?.patronOrders?.edges?.[0]?.node ?? null;
  const hasAppliedReward = ownerOrder?.squareLoyaltyRewards?.some((reward) =>
    isAppliedPatronLoyaltyReward(reward.status)
  );
  const rewardsOrder =
    ownerOrder && (ownerOrder.availableLoyaltyRewards || hasAppliedReward)
      ? ownerOrder
      : null;
  const enrollmentOrder = ownerOrder?.loyaltyEnrollmentOffer
    ? ownerOrder
    : null;
  const order =
    display === "rewards"
      ? rewardsOrder
      : display === "enrollment"
      ? enrollmentOrder
      : rewardsOrder ?? enrollmentOrder;
  const loyaltyRewards = ownerOrder?.squareLoyaltyRewards ?? [];

  if (!booking || !order) {
    return null;
  }

  return (
    <PatronCheckoutLoyaltyRewards
      analyticsProperties={{ bookingId: booking.id }}
      loyaltyRewards={loyaltyRewards}
      orderRef={order}
      enrollmentOfferRef={patronLoyalty ? order : null}
      display={display}
      editable={booking.status === "DRAFT"}
      section={section}
    />
  );
};

export const BookingLoyaltyRewardsBookingFragment = graphql`
  fragment BookingLoyaltyRewards_booking on Booking
  @argumentDefinitions(
    patronLoyalty: { type: "Boolean!", defaultValue: false }
  ) {
    id
    ... on StandardBooking {
      status
      patronOrders: orders(first: 1) {
        edges {
          node {
            availableLoyaltyRewards @include(if: $patronLoyalty) {
              balance
            }
            loyaltyEnrollmentOffer @include(if: $patronLoyalty) {
              points
            }
            squareLoyaltyRewards @include(if: $patronLoyalty) {
              id
              points
              status
              loyaltyCustomerId
              loyaltyAccount {
                id
              }
              rewardTier {
                id
                name
              }
            }
            ...PatronCheckoutLoyaltyRewards_order @include(if: $patronLoyalty)
            ...PatronCheckoutLoyaltyRewardsEnrollmentOffer_order
              @include(if: $patronLoyalty)
          }
        }
      }
    }
    ... on LessonBooking {
      status
      patronOrders: orders(first: 1) {
        edges {
          node {
            availableLoyaltyRewards @include(if: $patronLoyalty) {
              balance
            }
            loyaltyEnrollmentOffer @include(if: $patronLoyalty) {
              points
            }
            squareLoyaltyRewards @include(if: $patronLoyalty) {
              id
              points
              status
              loyaltyCustomerId
              loyaltyAccount {
                id
              }
              rewardTier {
                id
                name
              }
            }
            ...PatronCheckoutLoyaltyRewards_order @include(if: $patronLoyalty)
            ...PatronCheckoutLoyaltyRewardsEnrollmentOffer_order
              @include(if: $patronLoyalty)
          }
        }
      }
    }
  }
`;
